import { Box } from '@mui/material';
import { groupBy } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Bracket as ReactBracket } from 'react-brackets';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useDispatch, useSelector } from 'react-redux';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { ModalName } from 'app/enums';
import { LolTournamentEntity } from 'app/interfaces/entity';
import { modalsReducers } from 'app/store/modal/slice';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import { useStyles as scrollStyles } from 'shared/styles/ScrollBar.styles';
import { BRACKET_TRANSFORM_OPTIONS } from 'modules/bracket/constants';
import { ActiveMatchContext, BracketControlsContext } from '../../providers';
import { tournamentBracketSelector } from 'modules/bracket/store/selectors';
import { BracketSeed } from '../BracketSeed';
import { BracketHeader } from '../BracketHeader';

enum DoubleEliminationGroup {
  Upper,
  Lower,
  GrandFinal,
}

export const DoubleEliminationBracket: FC = () => {
  const dispatch = useDispatch();
  const [paddingLeft, setPaddingLeft] = useState(0);
  const { classes: scrollBarClasses } = scrollStyles();
  const rounds = useSelector(tournamentBracketSelector.rounds);
  const { onRefChange } = useContext(BracketControlsContext);
  const tournament = useSelector(currentTournamentSelector.tournament) as LolTournamentEntity;

  const { activeMatchId, setActiveMatchId } = useContext(ActiveMatchContext);

  const roundGroups = useMemo(() => groupBy(rounds, 'group'), [rounds]);

  const upperRounds = useMemo(() => roundGroups[DoubleEliminationGroup.Upper] ?? [], [roundGroups]);
  const lowerRounds = useMemo(() => roundGroups[DoubleEliminationGroup.Lower] ?? [], [roundGroups]);
  const grandFinal = useMemo(
    () => roundGroups[DoubleEliminationGroup.GrandFinal] ?? [],
    [roundGroups]
  );

  useEffect(() => {
    if (activeMatchId != null && !isEmpty(tournament)) {
      dispatch(
        modalsReducers.openModal({ name: ModalName.TournamentMatchDetails, data: activeMatchId })
      );
    }
  }, [activeMatchId, dispatch, tournament]);

  const onZoomHandler = useCallback(
    (event: ReactZoomPanPinchRef) => {
      setPaddingLeft(Math.abs(event.state.positionX));
    },
    [setPaddingLeft]
  );

  useEffect(() => {
    return () => setActiveMatchId(null);
  }, [setActiveMatchId]);

  if (!upperRounds?.length || !lowerRounds?.length || !grandFinal) {
    return null;
  }

  return (
    <TransformWrapper
      centerZoomedOut={true}
      ref={onRefChange}
      initialScale={BRACKET_TRANSFORM_OPTIONS.initialScale}
      minScale={BRACKET_TRANSFORM_OPTIONS.minScale}
      maxScale={BRACKET_TRANSFORM_OPTIONS.maxScale}
      doubleClick={{ disabled: true }}
      panning={{ disabled: true }}
      onZoom={onZoomHandler}
      wheel={{ wheelDisabled: true, step: 0.05 }}
      velocityAnimation={{ disabled: true, equalToMove: false }}
      zoomAnimation={{ disabled: true }}
    >
      <ScrollContainer
        vertical={false}
        horizontal={true}
        hideScrollbars={false}
        className={scrollBarClasses.horizontal}
      >
        <Box style={{ paddingLeft: `${paddingLeft}px` }}>
          <TransformComponent contentStyle={{ cursor: 'grab', width: 'auto' }}>
            <Box pr={1} pl={1} pt={2} pb={2}>
              <ReactBracket
                rounds={grandFinal}
                mobileBreakpoint={0}
                renderSeedComponent={BracketSeed}
                roundTitleComponent={(title, roundIndex) => {
                  const subMatchSettings = grandFinal[roundIndex]?.subMatchSettings;
                  return (
                    <BracketHeader
                      title={title}
                      roundIndex={roundIndex}
                      bestOf={subMatchSettings?.bestOf}
                      startedAt={subMatchSettings?.startedAt}
                      group={DoubleEliminationGroup.GrandFinal}
                    />
                  );
                }}
                swipeableProps={{ enableMouseEvents: true, ignoreNativeScroll: true }}
              />

              <ReactBracket
                rounds={upperRounds}
                mobileBreakpoint={0}
                renderSeedComponent={BracketSeed}
                roundTitleComponent={(title, roundIndex) => {
                  const subMatchSettings = upperRounds[roundIndex]?.subMatchSettings;
                  return (
                    <BracketHeader
                      title={title}
                      roundIndex={roundIndex}
                      bestOf={subMatchSettings.bestOf}
                      group={DoubleEliminationGroup.Upper}
                      startedAt={subMatchSettings.startedAt}
                    />
                  );
                }}
                swipeableProps={{ enableMouseEvents: true, ignoreNativeScroll: true }}
              />
              <ReactBracket
                rounds={lowerRounds}
                mobileBreakpoint={0}
                roundTitleComponent={(title, roundIndex) => {
                  const subMatchSettings = lowerRounds[roundIndex]?.subMatchSettings;
                  return (
                    <BracketHeader
                      title={title}
                      roundIndex={roundIndex}
                      bestOf={subMatchSettings.bestOf}
                      group={DoubleEliminationGroup.Lower}
                      startedAt={subMatchSettings.startedAt}
                    />
                  );
                }}
                renderSeedComponent={BracketSeed}
                swipeableProps={{ enableMouseEvents: true, ignoreNativeScroll: true }}
              />
            </Box>
          </TransformComponent>
        </Box>
      </ScrollContainer>
    </TransformWrapper>
  );
};
